import { toaster } from '@optiaxiom/react';

import http, { extractApiErrorMessage } from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';
import type { AgentVersionListResponse } from '@frontend/types';
import {
  Trigger,
  WorkflowAgent,
  WorkflowAgentExecution,
  WorkflowAgentTriggerResponse,
} from '@frontend/types/workflow-agent';

import {
  createEmailTrigger,
  deleteEmailTrigger,
  updateEmailTrigger,
} from '../components/workflow-agent/services/email-trigger';
import { convertToWorkflowAgent } from '../components/workflow-agent/services/graph-to-workflow';
import {
  createMessageTrigger,
  deleteMessageTrigger,
  updateMessageTrigger,
} from '../components/workflow-agent/services/message-trigger';
import {
  createScheduledTrigger,
  deleteScheduledTrigger,
  updateScheduledTrigger,
} from '../components/workflow-agent/services/scheduled-trigger';
import {
  createWebhookTrigger,
  deleteWebhookTrigger,
  updateWebhookTrigger,
} from '../components/workflow-agent/services/webhook-trigger';
import { triggerBlobDownload } from '../utils/downloadFileResponse';

import type { ImportValidationReport } from './import-validation-types';

export const createWorkflowTriggers = async (
  instanceId: string,
  workflowId: string,
  triggers: Trigger[]
) => {
  const triggerPromises: Array<{ promise: Promise<unknown>; type: string }> = [];

  // Create a mapping of trigger types to their creation functions
  const triggerCreators = {
    webhook: createWebhookTrigger,
    email: createEmailTrigger,
    scheduled: createScheduledTrigger,
    message: createMessageTrigger,
  } as const;

  // Loop through all triggers and push to triggerPromises based on type
  triggers.forEach(trigger => {
    if (trigger.type === 'webhook') {
      triggerPromises.push({
        promise: triggerCreators.webhook(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'email') {
      triggerPromises.push({
        promise: triggerCreators.email(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'scheduled') {
      triggerPromises.push({
        promise: triggerCreators.scheduled(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else if (trigger.type === 'message') {
      triggerPromises.push({
        promise: triggerCreators.message(
          workflowId,
          instanceId,
          trigger as unknown as Record<string, unknown>
        ),
        type: trigger.type,
      });
    } else {
      console.warn(`No creator function found for trigger type: ${(trigger as Trigger).type}`);
    }
  });

  if (triggerPromises.length > 0) {
    const results = await Promise.allSettled(triggerPromises.map(item => item.promise));

    results.forEach((result, index) => {
      const triggerType = triggerPromises[index].type;
      if (result.status === 'rejected') {
        console.error(`Failed to create ${triggerType} trigger:`, result.reason);
        toaster.create(extractApiErrorMessage(result.reason), { intent: 'danger' });
      }
    });
  }
};

export const createWorkflowAgent = async (
  agent: WorkflowAgent,
  instanceId: string
): Promise<WorkflowAgent> => {
  const data = convertToWorkflowAgent(agent.nodes || [], agent.edges || [], agent);

  try {
    const response = await http.post<WorkflowAgent>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows`,
      data.agentData
    );
    return response.data;
  } catch (error) {
    console.error('Failed to save workflow agent:', error);
    throw error;
  }
};

export const updateWorkflowAgent = async (
  agent: WorkflowAgent,
  updateUrl: string
): Promise<WorkflowAgent> => {
  try {
    const { agentData, triggers } = convertToWorkflowAgent(
      agent.nodes || [],
      agent.edges || [],
      agent
    );

    const updatePayload = {
      ...agentData,
      triggers,
    };

    const response = await http.put<WorkflowAgent>(updateUrl, updatePayload);
    return response.data;
  } catch (error) {
    console.error('Failed to update workflow agent:', error);
    throw error;
  }
};

export const getWorkflowAgents = (
  instanceId: string,
  page = 1,
  pageSize = 1000,
  includeDeleted = false,
  includeSteps = false
) => ({
  key: () =>
    ['opal:workflow-agents', instanceId, page, pageSize, includeDeleted, includeSteps] as const,
  queryFn: () => async (): Promise<WorkflowAgent[]> => {
    const response = await http.get<{ items: WorkflowAgent[] }>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows?page=${page}&page_size=${pageSize}&include_deleted=${includeDeleted}&include_steps=${includeSteps}`
    );
    return response.data.items || [];
  },
});

/**
 * Build the URL for a single workflow agent (GET). Callers pass this to
 * `useGetWorkflowAgent`, whose query key is `['opal:workflow-agent', url]` — so the
 * cache-invalidation key in `useUpdateWorkflowAgent` must build the URL the same way.
 */
export const getWorkflowAgentUrl = (instanceId: string, workflowId: string): string =>
  `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${workflowId}`;

export const getWorkflowAgent = (url: string) => async (): Promise<WorkflowAgent> => {
  const response = await http.get<WorkflowAgent>(url);
  return response.data;
};

export const deleteWorkflowAgent = async (deleteUrl: string): Promise<void> => {
  try {
    await http.delete(deleteUrl);
    return;
  } catch (error) {
    console.error('Failed to delete workflow agent:', error);
    throw error;
  }
};

export const triggerWorkflowAgent = async (
  instanceId: string,
  agentId: string,
  inputText: string
): Promise<WorkflowAgentTriggerResponse> => {
  try {
    const response = await http.post<WorkflowAgentTriggerResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/trigger`,
      inputText,
      {
        headers: {
          'Content-Type': 'text/plain',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Failed to trigger workflow agent:', error);
    throw new Error('Failed to trigger workflow agent');
  }
};

export const getWorkflowAgentExecution = async (
  instanceId: string,
  executionId: string
): Promise<WorkflowAgentExecution> => {
  const response = await http.get<WorkflowAgentExecution>(
    `${HYPATIA_URL}/api/v1/${instanceId}/workflow-executions/${executionId}`
  );
  return response.data;
};

export const exportWorkflowAgent = async (instanceId: string, agentId: string) => {
  const response = await http.get<{ data: unknown }>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/export`
  );
  return response.data.data;
};

export const validateWorkflowAgentImport = async (instanceId: string, agentData: object) => {
  const res = await http.post<{ data: ImportValidationReport }>(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/import/validate`,
    agentData
  );
  return res.data.data;
};

export const importWorkflowAgent = async (
  instanceId: string,
  agentData: object,
  mode: 'skip' | 'overwrite' = 'overwrite',
  conflictResolutions?: Record<string, string>,
  triggerConfigs?: Record<string, Record<string, unknown>>,
  directoryInstall?: { directory_agent_id: string; directory_version: number }
) => {
  const hasExtras =
    (conflictResolutions && Object.keys(conflictResolutions).length > 0) ||
    (triggerConfigs && Object.keys(triggerConfigs).length > 0) ||
    directoryInstall;

  if (hasExtras) {
    const res = await http.post(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/import?mode=${mode}`,
      {
        agent_data: agentData,
        conflict_resolutions: conflictResolutions ?? {},
        trigger_configs: triggerConfigs ?? {},
        ...(directoryInstall && {
          directory_agent_id: directoryInstall.directory_agent_id,
          directory_version: directoryInstall.directory_version,
        }),
      }
    );
    return res.data;
  }
  const res = await http.post(
    `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/import?mode=${mode}`,
    agentData
  );
  return res.data;
};

// Export workflow execution logs as CSV or Excel file download.
// When a date range is set, `limit` is omitted (the backend rejects limit+range)
// and the timeout is raised to accommodate the batched/ZIP path.
export async function exportWorkflowExecutionLogs(
  instanceId: string,
  workflowAgentId: string,
  fields: string[],
  format: 'csv' | 'xlsx' = 'csv',
  startDate?: string,
  endDate?: string,
  limit = 500
): Promise<void> {
  const hasRange = Boolean(startDate || endDate);
  const body: Record<string, unknown> = { workflow_agent_id: workflowAgentId, fields, format };
  if (hasRange) {
    if (startDate) body.start_date = startDate;
    if (endDate) body.end_date = endDate;
  } else {
    body.limit = limit;
  }

  const res = await http.post(
    `${HYPATIA_URL}/api/v1/${instanceId}/workflow-executions/export`,
    body,
    {
      responseType: 'blob',
      timeout: hasRange ? 10 * 60 * 1000 : 5 * 60 * 1000,
    }
  );

  triggerBlobDownload(
    res.data as Blob,
    res.headers['content-disposition'] as string | undefined,
    `workflow-executions.${format}`
  );
}

export function getWorkflowAgentVersions(
  instanceId: string,
  agentId: string,
  limit = 20,
  offset = 0
) {
  return async () => {
    const res = await http.get<AgentVersionListResponse>(
      `${HYPATIA_URL}/api/v1/${instanceId}/agents/workflows/${agentId}/versions`,
      { params: { limit, offset } }
    );
    return res.data;
  };
}

getWorkflowAgentVersions.key = (instanceId: string, agentId: string, limit = 20, offset = 0) =>
  ['opal:workflow-agent-versions', instanceId, agentId, limit, offset] as const;

export function getWorkflowAgentVersion(versionUrl: string) {
  return async () => {
    const res = await http.get<WorkflowAgent>(versionUrl);
    return res.data;
  };
}

getWorkflowAgentVersion.key = (versionUrl: string) =>
  ['opal:workflow-agent-version', versionUrl] as const;

export async function restoreWorkflowAgent(
  updateUrl: string,
  agentData: Record<string, unknown>
): Promise<WorkflowAgent> {
  const response = await http.put<WorkflowAgent>(updateUrl, agentData);
  return response.data;
}

export type PendingTriggerDeletion = {
  id: string;
  selfUrl: string;
  type: string;
};

export async function commitTriggerChanges(
  instanceId: string,
  agentId: string,
  triggerNodes: Array<{ data: Record<string, unknown>; type?: string; id?: string }>,
  pendingDeletions: PendingTriggerDeletion[]
): Promise<{ succeeded: number; failed: number; idRemap: Record<string, string> }> {
  const operations: Array<{ label: string; promise: Promise<unknown>; draftNodeId?: string }> = [];

  const creators: Record<
    string,
    (agentId: string, instanceId: string, data: Record<string, unknown>) => Promise<unknown>
  > = {
    webhook: createWebhookTrigger,
    email: createEmailTrigger,
    scheduled: createScheduledTrigger,
    message: createMessageTrigger,
  };

  const updaters: Record<string, (url: string, data: Record<string, unknown>) => Promise<unknown>> =
    {
      webhook: updateWebhookTrigger,
      email: updateEmailTrigger,
      scheduled: updateScheduledTrigger,
      message: updateMessageTrigger,
    };

  const deleters: Record<string, (url: string) => Promise<void>> = {
    webhook: deleteWebhookTrigger,
    email: deleteEmailTrigger,
    scheduled: deleteScheduledTrigger,
    message: deleteMessageTrigger,
  };

  for (const node of triggerNodes) {
    const data = node.data;
    const triggerType = (data.type as string) || node.type || '';
    const selfUrl = (data as Record<string, Record<string, string>>)?.links?.self;
    const isLocallyModified = data._locallyModified === true;

    const payload = { ...data };
    delete payload._locallyModified;

    // Scheduled trigger: apply data transformations before sending to API
    // (these were previously done in onAsyncSave but now deferred to commit time)
    if (triggerType === 'scheduled') {
      const scheduleType = payload.schedule_type as string;
      const endType = payload.end_type as string;

      if (scheduleType === 'one_time') {
        payload.cron_expression = null;
        payload.max_occurrences = null;
        payload.end_date = null;
        payload.start_time = null;
      } else if (scheduleType === 'recurring') {
        payload.scheduled_time = null;
        if (endType === 'max_occurrences') {
          payload.end_date = null;
        } else if (endType === 'end_date') {
          payload.max_occurrences = null;
        } else {
          payload.end_date = null;
          payload.max_occurrences = null;
        }
      }

      if (scheduleType === 'one_time' && payload.scheduled_time) {
        const { getUserTimezone, localToUtc } = await import(
          '../components/workflow-agent/utils/cron-utils'
        );
        const tz = getUserTimezone();
        payload.scheduled_time = localToUtc(payload.scheduled_time as string, tz);
        payload.timezone = tz;
      }

      if (scheduleType === 'recurring' && payload.start_time) {
        const raw = payload.start_time as string;
        const startUtc = new Date(raw.endsWith('Z') ? raw : `${raw}Z`);
        if (startUtc <= new Date()) {
          delete payload.start_time;
        }
      }

      delete payload.end_type;
    }

    if (!selfUrl) {
      const creator = creators[triggerType];
      if (creator) {
        operations.push({
          label: `create ${triggerType} "${data.name || ''}"`,
          promise: creator(agentId, instanceId, payload),
          draftNodeId: node.id,
        });
      }
    } else if (isLocallyModified) {
      const updater = updaters[triggerType];
      if (updater) {
        operations.push({
          label: `update ${triggerType} "${data.name || ''}"`,
          promise: updater(selfUrl, payload),
        });
      }
    }
  }

  for (const deletion of pendingDeletions) {
    const deleter = deleters[deletion.type];
    if (deleter) {
      operations.push({
        label: `delete ${deletion.type} "${deletion.id}"`,
        promise: deleter(deletion.selfUrl),
      });
    }
  }

  if (operations.length === 0) {
    return { succeeded: 0, failed: 0, idRemap: {} };
  }

  const results = await Promise.allSettled(operations.map(op => op.promise));

  let succeeded = 0;
  let failed = 0;
  const idRemap: Record<string, string> = {};
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded++;
      const op = operations[index];
      if (op.draftNodeId && result.value) {
        const created = result.value as Record<string, unknown>;
        const newId = (created.id as string) || '';
        if (newId && newId !== op.draftNodeId) {
          idRemap[op.draftNodeId] = newId;
        }
      }
    } else {
      failed++;
      console.error(`Trigger ${operations[index].label} failed:`, result.reason);
      toaster.create(`Failed to ${operations[index].label}`, { intent: 'danger' });
    }
  });

  return { succeeded, failed, idRemap };
}
